"use client";

import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { UNSAFE_PortalProvider } from "react-aria";
import { RouterProvider } from "react-aria-components";

import { NOWADAYS_UI_SCOPE_CLASS } from "./_logic/constants";
import { cn } from "./lib/utils";

interface NowadaysUiScopeProps {
  children: React.ReactNode;
  /** Classes for the wrapping element (a background, a min height). */
  className?: string;
}

/**
 * Turns on the Nowadays design tokens for a screen built from `@/nowadays-ui`. Inside it,
 * `primary`, the radius scale, the easing curves and the base border color take the venues
 * values instead of daisyUI's and Tailwind's (see `styles/theme.css`).
 *
 * React Aria renders menus, popovers and dialogs into `document.body`, outside this wrapper, so
 * it also gives them a container of their own on `body` that carries the scope class. Until that
 * container mounts (the first client render), overlays fall back to `body`. And it hands React
 * Aria the Next.js router, so `LinkButton` and other React Aria links navigate client-side instead
 * of reloading the page.
 * @param props - See {@link NowadaysUiScopeProps}.
 * @returns The scoped wrapper.
 */
export function NowadaysUiScope({ children, className }: NowadaysUiScopeProps): React.ReactElement {
  // Hooks
  const router = useRouter();

  // States
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

  // Effects
  useEffect(() => {
    const container = document.createElement("div");
    container.className = NOWADAYS_UI_SCOPE_CLASS;
    document.body.appendChild(container);
    setPortalContainer(container);
    return () => container.remove();
  }, []);

  // Render
  return (
    <div className={cn(NOWADAYS_UI_SCOPE_CLASS, className)}>
      <RouterProvider navigate={router.push}>
        <UNSAFE_PortalProvider getContainer={() => portalContainer}>{children}</UNSAFE_PortalProvider>
      </RouterProvider>
    </div>
  );
}
